import {
  useSetSharePointResourcePermissionsMutation,
  SetSharePointPermissionsRequest,
  SetSharePointResourcePermissionsMutationFn,
  SetSharePointResourcePermissionsMutation,
  SetSharePointResourcePermissionsInput,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { QueryResult } from '@apollo/client'
import {
  Permission,
  SharePointFolderReference,
  SharePointParentFolder,
  SharePointParentFolderItem,
} from '@cims-online/shared'
import {
  CustomRowActionOptions,
  CustomRowActionButton,
} from '../../../CIMSTableContext/types/actions/row'

export function useLockRowActionButton<
  T extends LockUnlockRowActionButtonFields
>({
  refetch,
  parent,
  hidden,
}: LockUnlockRowActionButtonOptions<T>): CustomRowActionOptions<T> {
  const [set] = useSetSharePointResourcePermissionsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: (data) =>
      onCompleted(SetSharePointPermissionsRequest.Lock, data),
  })
  return {
    text: 'Lock',
    permission: getPermission(parent),
    onClick: async (record) =>
      await resolveClick({
        parentId: parent.id,
        record,
        set,
        refetch,
        request: SetSharePointPermissionsRequest.Lock,
      }),
    hidden: (record) => resolveHidden(record, hidden),
  }
}

export function useUnlockRowActionButton<
  T extends LockUnlockRowActionButtonFields
>({
  refetch,
  parent,
  hidden,
}: LockUnlockRowActionButtonOptions<T>): CustomRowActionOptions<T> {
  const [set] = useSetSharePointResourcePermissionsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: (data) =>
      onCompleted(SetSharePointPermissionsRequest.Unlock, data),
  })
  return {
    text: 'Unlock',
    permission: getPermission(parent),
    onClick: async (record) =>
      await resolveClick({
        parentId: parent.id,
        record,
        refetch,
        set,
        request: SetSharePointPermissionsRequest.Unlock,
      }),
    hidden: (record) => resolveHidden(record, hidden),
  }
}

function onCompleted(
  request: SetSharePointPermissionsRequest,
  {
    setSharePointResourcePermissions: {
      user,
      constructionAdminSubmittal,
      concreteTestSet,
      designSubmittal,
      fieldDesignChange,
      requestForInformation,
      issue,
    },
  }: SetSharePointResourcePermissionsMutation
) {
  const record =
    constructionAdminSubmittal?.sharePointFolderName ??
    concreteTestSet?.testSetId ??
    concreteTestSet?.globalId ??
    designSubmittal?.sharePointFolderName ??
    (fieldDesignChange ?? requestForInformation ?? issue)?.formattedNumber
  if (!record) throw new Error('Lock/Unlock not completely set up for record')
  const detail =
    request === SetSharePointPermissionsRequest.Lock
      ? 'locked'
      : `unlocked for ${user?.displayName ?? user.mail}`
  simpleSuccessNotification(`${record} successfully ${detail}!`)
}

function getPermission({ id, name }: SharePointParentFolderItem) {
  let permission
  if (id === SharePointParentFolder.FieldDesignChanges.id)
    permission = Permission.UnlockFieldDesignChange
  else if (id === SharePointParentFolder.ConstructionAdminSubmittals.id)
    permission = Permission.UnlockConstructionAdminSubmittal
  else if (id === SharePointParentFolder.DesignSubmittals.id)
    permission = Permission.UnlockDesignSubmittal
  else if (id === SharePointParentFolder.RequestForInformation.id)
    permission = Permission.UnlockRequestForInformation
  else if (id === SharePointParentFolder.Issues.id)
    permission = Permission.UnlockIssue
  else throw new Error(`Lock/Unlock not completely set up for ${name}`)
  return permission
}

function resolveHidden<T extends LockUnlockRowActionButtonFields>(
  t: T,
  hidden: CustomRowActionButton<T>['hidden']
) {
  return (
    (typeof hidden === 'function' ? hidden(t) : hidden) || !t.sharePointResource
  )
}

interface ResolveClickOptions<T>
  extends Pick<SetSharePointResourcePermissionsInput, 'request' | 'parentId'>,
    Required<Pick<QueryResult, 'refetch'>> {
  record: T | undefined
  set: SetSharePointResourcePermissionsMutationFn
}

async function resolveClick<T extends LockUnlockRowActionButtonFields>({
  record,
  refetch,
  request,
  set,
  parentId,
}: ResolveClickOptions<T>) {
  const { id, sharePointResource } = record ?? {}
  if (!sharePointResource?.id || id === undefined) return
  await set({
    variables: {
      input: {
        resourceId: sharePointResource.id,
        recordId: id.toString(),
        parentId,
        request,
      },
    },
  })
  await refetch()
}

interface LockUnlockRowActionButtonFields {
  id?: string | number
  sharePointResource?: {
    id: string
  } | null
}

interface LockUnlockRowActionButtonOptions<T>
  extends Required<Pick<QueryResult, 'refetch'>>,
    Pick<SharePointFolderReference, 'parent'> {
  /**
   * if there isn't an associated {@link LockUnlockRowActionButtonFields.sharePointResource},
   * the button will be hidden
   *
   * add any additional logic to execute in order to determine whether the button
   * should be hidden from users
   */
  hidden: (record: Omit<T, 'sharePointResource'>) => boolean
}
